import type { ReactElement } from "react";
import { controlCharsObject, notDisplayedKeywords } from "@quickpoint.me/shared";
import type { ElementProps } from "../functionalComponents/RenderPage";
import type { GridObject } from "./gridTypes";

export function calculateResponsiveTable(elements: ReactElement<ElementProps>[], isInLandscape: boolean): GridObject[] {
  const gridArray: GridObject[] = [];
  const titleElements: ReactElement<ElementProps>[] = [];
  const dataElements: ReactElement<ElementProps>[] = [];

  const firstSeparatorIndex = elements.findIndex(el => el.props.type === controlCharsObject["---"].controlChar);
  if (firstSeparatorIndex === -1) return [];

  const secondSeparatorIndex = elements.findIndex((el, index) => index > firstSeparatorIndex && el.props.type === controlCharsObject["---"].controlChar);
  if (secondSeparatorIndex === -1) return [];

  const titleSlice = elements.slice(firstSeparatorIndex + 1, secondSeparatorIndex);
  const dataSlice = elements.slice(secondSeparatorIndex + 1);

  for (const el of titleSlice) {
    if (el.props.type === "columnTitle:") {
      titleElements.push(el);
    }
  }

  for (const el of dataSlice) {
    if (
      el.props.type !== controlCharsObject["---"].controlChar &&
      el.props.type !== controlCharsObject["-->"].controlChar &&
      !notDisplayedKeywords.includes(el.props.type as string) &&
      !el.props.isAutospaceElement
    ) {
      dataElements.push(el);
    }
  }

  const numColumns = titleElements.length;
  if (numColumns === 0) return [];

  const dataRows: ReactElement<ElementProps>[][] = [];
  for (let i = 0; i < dataElements.length; i += numColumns) {
    dataRows.push(dataElements.slice(i, i + numColumns));
  }

  if (isInLandscape) {
    titleElements.forEach((el, colIndex) => {
      gridArray.push({
        col: colIndex + 1,
        row: 1,
        elArray: [el],
        isResponsiveTable: true,
        maxCols: numColumns,
        maxRows: dataRows.length + 1,
        lastColInRow: colIndex === numColumns - 1,
        lastRowInGrid: dataRows.length === 0,
        isSplitCell: false,
      });
    });
    dataRows.forEach((rowElements, rowIndex) => {
      rowElements.forEach((el, colIndex) => {
        gridArray.push({
          col: colIndex + 1,
          row: rowIndex + 2,
          elArray: [el],
          isResponsiveTable: true,
          maxCols: numColumns,
          maxRows: dataRows.length + 1,
          lastColInRow: colIndex === rowElements.length - 1,
          lastRowInGrid: rowIndex === dataRows.length - 1,
          isSplitCell: false,
        });
      });
    });
  } else { // Portrait Mode - 2 column layout
    let rowIndex = 1;
    const maxCols = 2;
    const totalRows = dataElements.length;
    for (const rowElements of dataRows) {
      for (const [colIndex, el] of rowElements.entries()) {
        if (colIndex < titleElements.length) {
          const titleElement = titleElements[colIndex];
          gridArray.push({
            col: 1,
            row: rowIndex,
            elArray: [titleElement],
            isResponsiveTable: true,
            maxCols,
            maxRows: totalRows,
            lastColInRow: false,
            lastRowInGrid: false,
            isSplitCell: false,
          });
          gridArray.push({
            col: 2,
            row: rowIndex,
            elArray: [el],
            isResponsiveTable: true,
            maxCols,
            maxRows: totalRows,
            lastColInRow: true,
            lastRowInGrid: false,
            isSplitCell: false,
          });
          rowIndex++;
        }
      }
      if (dataRows.indexOf(rowElements) < dataRows.length - 1) {
        gridArray.push({
          col: 1,
          row: rowIndex,
          elArray: [], // Empty cell for spacing
          isResponsiveTable: true,
          isSpacer: true,
          maxCols: 1,
          maxRows: totalRows,
          lastColInRow: true,
          lastRowInGrid: false,
          isSplitCell: true,
        });
        rowIndex++;
      }
    }
    if (gridArray.length > 0) {
      gridArray[gridArray.length - 1].lastRowInGrid = true;
    }
  }

  return gridArray;
}
